import {useCallback, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams, createSearchParams} from 'react-router-dom';

import indexedDbStorage from '../utils/indexedDbStorage';
import miaFlowAuth from '../auth/MiaFlowAuth';
import miaPlatformDataService from '../services/MiaPlatformDataService';
import {encodePathSegment, encodeServiceNameForUrl} from '../utils/urlPathSegment';
import {createQuickVfsProject} from '../utils/quickProjectCreation';
import {readProjectMetadata} from '../utils/vfsClient';
import { devLog } from '../utils/devLog';
import {filesPathFromSearch} from '../utils/filesPath';
import {viewFromSearch} from '../utils/workspaceModel';
import {codeUrlInTenant, selectionSyncTarget} from '../utils/codeRoute';

// Waits before each new try when a project could not be loaded for a reason other than 404/403 (2 s, 5 s, 10 s).
const PROJECT_LOAD_RETRIES_MS = [2000, 5000, 10000];


/**
 * Owns MainChat's `canvasProjectContext`/`urlValidationError`/
 * `showProjectSelector` and every effect/listener that writes them, per the
 * DDD refactor plan (tender-roaming-bentley.md, Phase 4) — this is the fix
 * for the "five separate effects independently decide what project/URL
 * state should be" race the plan identifies as the root of several bugs
 * chased earlier this session (silent URL rewrites, "Project Not Found"
 * flashes, stale-project self-heal races).
 *
 * Extracted VERBATIM from MainChat (App.jsx) — every effect below is a pure
 * relocation, same trigger conditions, same bodies, same dependency arrays.
 * No behavior change is intended. The six writers consolidated here (the
 * plan names five URL/localStorage/IndexedDB sync effects; a sixth,
 * event-driven `select-project` listener writes the exact same state and is
 * included for the same "one clear owner" reason, not part of the plan's
 * original five):
 *   1. Redirect from IndexedDB-saved context on a bare `/code` visit.
 *   2. Redirect from `miaFlowAuth`'s stored tenant/project on a bare `/code`
 *      visit (runs alongside #1; mutually exclusive trigger conditions, not
 *      a competing writer of the same case).
 *   3. The big one: validate `tenantId`/`projectId` URL params, load the
 *      project/config, or auto-create an vfs project when none is
 *      selected.
 *   4. Load project context from a `canvasMode`/`canvasProjectContext`
 *      localStorage flag on a bare `/code` visit (set by other pages before
 *      navigating here).
 *   5. Reconstruct the URL's microservice/branch/tab segments from
 *      localStorage when the URL only has `tenantId`/`projectId`.
 *   6. `select-project` window-event listener (dispatched by Home/Memories).
 *
 * Exposes `syncUrlWithCanvasSelections` as the single write path Canvas.jsx
 * calls on every selection change (`onSelectionChange` prop), and
 * `handleProjectContextChange` for Canvas's `onProjectContextChange` prop —
 * matching the plan's explicit callout that these should be "the one write
 * path other consumers call."
 *
 * `showToast` and `setAiMode` are taken as explicit parameters rather than
 * closed over — they're owned by pieces of MainChat not yet extracted.
 */
export function useCanvasProjectContext({showToast, setAiMode}) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const [canvasProjectContext, setCanvasProjectContext] = useState(null);
    const [urlValidationError, setUrlValidationError] = useState(null);
    const [showProjectSelector, setShowProjectSelector] = useState(false);

    // Track last loaded project to prevent redundant fetches (effect #3).
    const lastLoadedProjectRef = useRef(null);

    // Guards effect #3's async `validateAndLoadProject` against a real,
    // live-confirmed race: it has multiple `await`s (getFullProjectConfiguration
    // -> getRevisions -> getMicroservices) with no cancellation, so navigating
    // to a SECOND project before the FIRST project's fetch chain finishes
    // starts a second, overlapping `validateAndLoadProject` call — and
    // whichever one's network round-trip happens to resolve LAST wins the
    // final `setCanvasProjectContext` call, regardless of which one is for
    // the project the URL/UI actually shows now. Confirmed live: URL showed
    // one project, the breadcrumb name showed a second, and the Files
    // browser (which reads `selectedProject.id`, itself derived from this
    // same context) showed a THIRD project's real content — three different
    // async responses, resolved out of order, each partially overwriting
    // the last. Bumped once per effect run; every `setCanvasProjectContext`
    // call inside checks it's still the most recent request before applying
    // its result, so a stale/superseded response is silently discarded
    // instead of winning the race.
    const projectLoadRequestIdRef = useRef(0);

    // --- Effect #1: redirect from IndexedDB-saved context on bare /code ---
    useEffect(() => {
        const loadCanvasContext = async () => {
            // ONLY load from IndexedDB if we're on /code with NO parameters
            // If URL has project info, let the URL validation effect handle it
            if (location.pathname !== '/code' && location.pathname !== '/code/') {
                return;
            }

            try {
                // Try to migrate from localStorage first
                await indexedDbStorage.migrateFromLocalStorage('canvasProjectContext');

                // Load from IndexedDB
                const savedContext = await indexedDbStorage.getItem('canvasProjectContext');
                if (savedContext && savedContext.projectId) {
                    // Redirect to the saved project URL instead of loading context directly
                    const tenantId = savedContext.tenant?.tenantId || savedContext.tenant?.id;
                    const projectId = savedContext.projectId;

                    if (tenantId && projectId) {
                        // Preserve conversationId query parameter if it exists
                        const searchParams = createSearchParams(location.search);
                        const conversationIdParam = searchParams.get('conversationId');
                        const path = `/code/${tenantId}/${projectId}`;
                        const finalPath = conversationIdParam ? `${path}?conversationId=${conversationIdParam}` : path;

                        devLog('🔗 Redirecting to saved project from IndexedDB');
                        // Forward location.state (e.g. Home's {initialPrompt, mode,
                        // initialFiles}) through this internal redirect — task #165:
                        // a bare `/code` visit carrying a Home prompt was silently
                        // dropping it here before the initialPrompt effect (App.jsx)
                        // ever saw it, since this replace-navigate previously passed
                        // no state at all.
                        navigate(finalPath, { replace: true, state: location.state });
                    }
                }
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Failed to load Canvas project context:', error);
            }
        };
        loadCanvasContext();
    }, [location.pathname, navigate]);

    // --- Effect #2: redirect from stored tenant/project on bare /code ---
    useEffect(() => {
        if (location.pathname === '/code' || location.pathname === '/code/') {
            const storedTenant = miaFlowAuth.getSelectedTenant();
            const storedProject = miaFlowAuth.getSelectedProject();

            if (storedTenant && storedProject) {
                // Redirect to URL with tenant and project. Marked autoRedirected
                // so the validation effect below knows this projectId came from
                // an assumption (last-used project), not something the user
                // explicitly navigated to — if it turns out stale, it should
                // self-heal quietly instead of flashing "Project Not Found".
                // Task #165: forward location.state (Home's initialPrompt/mode/
                // initialFiles) through this internal redirect too — see the
                // matching fix/comment on the IndexedDB redirect above.
                navigate(`/code/${storedTenant.tenantId}/${storedProject.projectId}`, { replace: true, state: { ...location.state, autoRedirected: true } });
            } else if (storedTenant) {
                // Redirect to URL with just tenant
                navigate(`/code/${storedTenant.tenantId}`, { replace: true });
            } else {
                // No tenant or project selected - redirect to projects page
                navigate('/projects', { replace: true });
            }
        }
    }, [location.pathname, navigate]);

    // --- Effect #3: validate URL parameters (tenantId and projectId) ---
    useEffect(() => {
        // Only validate on /code route
        if (!location.pathname.startsWith('/code')) {
            return;
        }

        // Get tenant and project from path params or stored state
        const urlTenantId = params.tenantId;
        const projectId = params.projectId;
        const storedTenant = miaFlowAuth.getSelectedTenant();
        const storedProject = miaFlowAuth.getSelectedProject();

        // Check both URL parameter and app state for tenant
        const tenantId = urlTenantId || storedTenant?.tenantId;

        // Skip if we already loaded this exact project (prevent re-fetching)
        const projectKey = `${tenantId}-${projectId}`;
        if (lastLoadedProjectRef.current === projectKey) {
            devLog('⏭️ Skipping URL validation - project already loaded:', projectKey);
            return;
        }

        // Check if tenantId is missing from both URL and state
        if (!tenantId) {
            setUrlValidationError({
                type: 'missing_tenant',
                message: 'You must be added to a tenant to access this application.',
                action: 'Go to Home'
            });
            return;
        }

        // Clear any previous error if tenantId is present
        if (urlValidationError?.type === 'missing_tenant') {
            setUrlValidationError(null);
        }

        // Check if projectId is missing
        if (!projectId) {
            // Check if there's a previously selected project
            if (storedProject && storedProject.projectId) {
                // Auto-navigate to the stored project instead of showing selector
                devLog('📂 Auto-loading stored project:', storedProject.name || storedProject.projectId);
                // Task #165: forward location.state (Home's initialPrompt/mode/
                // initialFiles) — same fix as the other internal redirects above.
                navigate(`/code/${tenantId}/${storedProject.projectId}`, { replace: true, state: { ...location.state, autoRedirected: true } });
                return;
            }

            // No stored "last project" — instead of landing on a manual picker,
            // create a fresh vfs vfs-backed project on the fly and jump
            // straight into it (real per-task requirement: "If no last create
            // on the fly the project on vfs"). The manual picker is still
            // reachable as an explicit escape hatch — e.g. from the "Project
            // Not Found"/"tenant mismatch" error screens' "Choose Another
            // Project" action, or via Home/Memories — it's just no longer the
            // automatic landing state for a bare /code visit.
            const createOnTheFly = async () => {
                try {
                    const { projectId: newProjectId, name } = await createQuickVfsProject(tenantId);
                    miaFlowAuth.setSelectedProject({ projectId: newProjectId, name });
                    devLog('✨ Created vfs project on the fly:', newProjectId);
                    // Task #165: real live bug — Home's default "Code" flow
                    // (handleStartChatFromHome's mode==='code' branch) navigates to
                    // bare `/code` with state:{initialPrompt, mode, initialFiles}
                    // because there's no project yet to route to directly. This
                    // effect creates that first project right here, then used to
                    // redirect with NO state at all — silently dropping the Home
                    // prompt before App.jsx's initialPrompt effect (which only ever
                    // sees the FINAL location) could read it. Confirmed live: the
                    // project got created (real URL, real empty file tree) but the
                    // chat panel never received a message and no code was ever
                    // written — exactly the reported symptom. Forward it through.
                    navigate(`/code/${tenantId}/${newProjectId}`, { replace: true, state: location.state });
                } catch (error) {
                    console.error('Failed to create project on the fly:', error);
                    showToast('Failed to create a new project — pick an existing one instead', 'error');
                    // task #126: the standalone "Select a Project" interstitial
                    // (ProjectPickerModal mode="select") was removed per explicit
                    // user instruction — the real Projects list (/projects)
                    // already does everything it did, so fall back there instead.
                    navigate('/projects', { replace: true });
                }
            };
            createOnTheFly();
        } else {
            setShowProjectSelector(false);
            setUrlValidationError(null); // Clear any stale error from previous navigation
            // Both tenantId and projectId are present, validate and load project
            const validateAndLoadProject = async (attempt = 0) => {
                // Claim this call as the current/latest project-load request —
                // see projectLoadRequestIdRef's own doc above for the race this
                // guards against. Every state-mutating step below re-checks
                // this before applying its result, so a response that arrives
                // after a NEWER navigation has already started a later request
                // is discarded instead of overwriting the newer one's state.
                const requestId = ++projectLoadRequestIdRef.current;
                const isStale = () => projectLoadRequestIdRef.current !== requestId;
                try {
                    const projectConfig = await miaPlatformDataService.getFullProjectConfiguration(projectId);
                    if (isStale()) return;

                    // The project lives in another organization than the URL's (a workspace, an email or a
                    // notification link built with the selected organization): the person could read it, so open it
                    // there, selecting that organization when they are in it, instead of refusing.
                    const projectTenantId = projectConfig.info?.tenantId;
                    const moved = projectTenantId ? codeUrlInTenant(location, tenantId, projectTenantId) : null;
                    if (moved && attempt === 0) {
                        const tenants = await miaPlatformDataService.getTenants().catch(() => []);
                        if (isStale()) return;
                        const own = (tenants || []).map((item) => miaPlatformDataService.formatTenant(item))
                            .find((item) => item?.tenantId === projectTenantId);
                        // setSelectedTenant also announces it (mia-flow-tenant-changed): the header follows.
                        if (own) { miaFlowAuth.setSelectedTenant(own); }
                        navigate(moved, { replace: true, state: location.state });
                        return;
                    }
                    // Verify project exists (if config has tenant info, check it)
                    if (projectTenantId && projectTenantId !== tenantId) {
                        setUrlValidationError({
                            type: 'tenant_mismatch',
                            message: `Project does not belong to the specified tenant.`,
                            action: 'Go to Home'
                        });
                        return;
                    }

                    // Fetch revisions for the project
                    const revisions = await miaPlatformDataService.getRevisions(projectId);
                    if (isStale()) return;

                    // Use the first revision in the list
                    if (!revisions || revisions.length === 0) {
                        setUrlValidationError({
                            type: 'project_not_found',
                            message: `No revisions found for this project.`,
                            action: 'Choose Another Project'
                        });
                        return;
                    }

                    const selectedRevision = revisions[0];
                    devLog('📝 Using first revision:', selectedRevision);

                    // Extract revision identifier (revision objects have 'name' property, e.g., 'main', 'master')
                    const revisionId = selectedRevision.name || selectedRevision.revision || selectedRevision._id;
                    devLog('📝 Extracted revision ID:', revisionId);

                    // Fetch microservices/configuration for the selected revision
                    const configuration = await miaPlatformDataService.getMicroservices(
                        projectId,
                        revisionId
                    );
                    if (isStale()) return;

                    // Set up canvas project context with full data
                    // Clear selected resource from previous project
                    localStorage.removeItem('mia_selectedResource');

                    const newContext = {
                        projectId: projectId,
                        project: {
                            _id: projectId,
                            id: projectId,  // Add explicit id field for Canvas
                            projectId: projectId,
                            name: projectConfig.info?.name || projectConfig.name || 'Project',
                            description: projectConfig.info?.description || projectConfig.description || '',
                            tenantId: tenantId,
                            environments: projectConfig.environments || [],
                            repositoryUrl: projectConfig.configurationGitPath || projectConfig.repositoryUrl || null,
                            configurationGitPath: projectConfig.configurationGitPath || null
                        },
                        tenant: {
                            id: tenantId,
                            name: projectConfig.info?.tenantName || projectConfig.tenantName || tenantId,
                            tenantId: tenantId
                        },
                        revision: {
                            revision: revisionId,
                            revisionId: selectedRevision._id || revisionId,
                            name: selectedRevision.name || revisionId,
                            ...selectedRevision
                        },
                        selectedResource: null,
                        selectedMicroservice: null,
                        configuration: configuration
                    };

                    devLog('📦 Created canvas context:', {
                        projectId: newContext.projectId,
                        projectName: newContext.project.name,
                        revisionId: newContext.revision.revisionId
                    });

                    setCanvasProjectContext(newContext);
                    await indexedDbStorage.setItem('canvasProjectContext', newContext);
                    setUrlValidationError(null); // Ensure no stale error remains after successful load

                    // Remember this as the last-used project (real bug fix,
                    // task #126): this successful-load path never called
                    // miaFlowAuth.setSelectedProject before, so Effect #3's
                    // own "no stored project" fallback above (and the Code
                    // sidebar icon's bare /code navigation) always saw a
                    // stale/empty stored project and created a brand new
                    // vfs one on every single visit, regardless of
                    // whatever project was actually just open — confirmed
                    // live via the Projects list filling up with a dozen+
                    // duplicate "Untitled Project" entries created seconds
                    // apart. setSelectedProject was previously only called
                    // from the on-the-fly-creation fallback and the manual
                    // ProjectPickerModal, never from this, the actual normal
                    // "open a project via its real URL" path.
                    miaFlowAuth.setSelectedProject({ projectId, name: newContext.project.name });

                    // Mark this project as loaded to prevent re-fetching
                    lastLoadedProjectRef.current = `${tenantId}-${projectId}`;

                    // DON'T clear the flag here - Canvas needs to finish initializing
                    // The flag will be cleared by Canvas after it's done with initial setup

                    devLog('✅ Loaded project from URL parameters:', newContext.project);

                    // Resume the conversation this project was created from, if
                    // one's recorded on it (`.mia/project.json`'s conversationId
                    // — set by adk-be-app's vfs_set_project_metadata) and the
                    // URL doesn't already name a specific conversation to open
                    // instead (e.g. a deliberate Memories/Home link to a
                    // DIFFERENT past session for this same project — never
                    // override an explicit choice already in the URL). Without
                    // this, opening a project via Projects/the sidebar landed
                    // on a fresh, unbound chat even though the exact
                    // conversation that built it is right there on disk.
                    // Best-effort: readProjectMetadata already resolves to
                    // null for a non-vfs project, a project with no metadata
                    // file, or any read failure — nothing to redirect to in
                    // any of those cases, and that's fine.
                    if (!new URLSearchParams(location.search).get('conversationId')) {
                        readProjectMetadata(projectId).then((metadata) => {
                            if (isStale() || !metadata?.conversationId) { return; }
                            const params = new URLSearchParams(location.search);
                            params.set('conversationId', metadata.conversationId);
                            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
                        }).catch(() => {
                            // Best-effort — see the comment above.
                        });
                    }

                    // Check for pending brainstorming prompt - auto-trigger after config is pulled
                    const pendingPrompt = sessionStorage.getItem('mia_pending_initial_prompt');
                    const pendingMode = sessionStorage.getItem('mia_pending_initial_mode');

                    if (pendingPrompt && pendingMode === 'brainstorming') {
                        const configRepoUrl = projectConfig.configurationGitPath || projectConfig.repositoryUrl;

                        if (!configRepoUrl) {
                            // No config repo — fire immediately (gitPullCompleted won't come)
                            sessionStorage.removeItem('mia_pending_initial_prompt');
                            sessionStorage.removeItem('mia_pending_initial_mode');

                            const brainstormingPrompt = `Create a file named brainstorming.md that answers the following user request: "${pendingPrompt}"\n\nIn brainstorming.md, explore what is available in the Mia Platform catalog/marketplace to satisfy the request. Structure the document with:\n1. **Request Summary**: What the user wants to achieve\n2. **Available Mia Platform Components**: Relevant services, plugins, templates from the Mia catalog that can be used\n3. **Suggested Architecture**: How to combine these components to fulfill the request\n4. **Next Steps**: Concrete actions to implement the solution using Mia Platform`;

                            devLog('🧠 [Brainstorming] No config repo – firing prompt directly');

                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('populate-chat-input', {
                                    detail: { message: brainstormingPrompt, autoSend: true }
                                }));
                            }, 1500);
                        }
                        // else: gitPullCompleted listener in MainChat will fire the prompt after config is pulled
                    }

                } catch (error) {
                    console.error('Failed to load project from URL:', error);
                    if (isStale()) return;

                    // Only a 404 or 403 means the project is missing or not shared. Anything else (the sandbox waking
                    // up or being updated, a gateway timeout, the network) is retried a few times, then reported as
                    // "could not open", never as "not found".
                    const missing = error?.status === 404 || error?.status === 403;
                    const signedOut = error?.status === 401;
                    if (!missing && !signedOut && attempt < PROJECT_LOAD_RETRIES_MS.length) {
                        setTimeout(() => {
                            if (!isStale()) { validateAndLoadProject(attempt + 1); }
                        }, PROJECT_LOAD_RETRIES_MS[attempt]);
                        return;
                    }

                    // This projectId came from an automatic redirect to a
                    // previously-stored "last project" reference (see the two
                    // navigate(..., {state: {autoRedirected: true}}) call sites
                    // above), not something the user explicitly typed/clicked
                    // this time. A stale reference (e.g. the project was
                    // deleted) shouldn't flash a scary full-page "Project Not
                    // Found" screen — silently recover into a fresh vfs
                    // project instead. A genuinely user-initiated navigation to
                    // a bad/inaccessible project id still shows the real error
                    // below, unchanged.
                    if (location.state?.autoRedirected) {
                        console.warn('⚠️ Stored project reference is stale, clearing and creating a fresh one instead of showing an error:', projectId);
                        miaFlowAuth.setSelectedProject(null);
                        try {
                            const { projectId: newProjectId, name } = await createQuickVfsProject(tenantId);
                            miaFlowAuth.setSelectedProject({ projectId: newProjectId, name });
                            // Task #165: forward location.state here too, for the same
                            // reason as the other internal redirects in this file.
                            navigate(`/code/${tenantId}/${newProjectId}`, { replace: true, state: location.state });
                            return;
                        } catch (recoveryError) {
                            console.error('Failed to recover from stale stored project:', recoveryError);
                            // fall through to the real error below
                        }
                    }

                    if (signedOut) {
                        setUrlValidationError({ type: 'session_expired', message: 'Your Flow session has expired. Sign in again to open this project; it is safe.' });
                    } else {
                        setUrlValidationError(missing
                            ? { type: 'project_not_found', message: 'This project does not exist, or it is not shared with you.' }
                            : { type: 'project_load_failed', message: 'Your sandbox did not answer in time. It may be waking up or being updated.' });
                    }
                }
            };

            // Always validate and load project from URL - URL is source of truth
            validateAndLoadProject();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, params.tenantId, params.projectId, showToast]);

    // --- Opens the project picker on demand (UnifiedTopBar's ProjectBreadcrumb) ---
    useEffect(() => {
        const handleOpenProjectPicker = () => setShowProjectSelector(true);
        window.addEventListener('mia-open-project-picker', handleOpenProjectPicker);
        return () => window.removeEventListener('mia-open-project-picker', handleOpenProjectPicker);
    }, []);

    // --- Effect #4: load project context from localStorage on bare /code ---
    useEffect(() => {
        const loadProjectFromLocalStorage = async () => {
            // Only load from localStorage if we're on /code WITHOUT URL parameters
            // If there are URL params (tenantId/projectId), let the URL validation effect handle it
            if (location.pathname === '/code' && !params.tenantId && !params.projectId) {
                // Check if there's a project context in localStorage (from project selection)
                const projectContextStr = localStorage.getItem('canvasProjectContext');
                const canvasModeStr = localStorage.getItem('canvasMode');

                if (projectContextStr && canvasModeStr === 'true') {
                    try {
                        const projectContext = JSON.parse(projectContextStr);

                        // Set the project context
                        setCanvasProjectContext(projectContext);

                        // Canvas is always enabled, no need to set it

                        // Save to IndexedDB for persistence
                        await indexedDbStorage.setItem('canvasProjectContext', projectContext);

                        devLog('📋 Loaded project from localStorage:', projectContext);

                        // Clean up localStorage flag immediately to prevent re-processing
                        localStorage.removeItem('canvasProjectContext');
                    } catch (error) {
                        console.error('Failed to load project context:', error);
                        localStorage.removeItem('canvasProjectContext');
                    }
                }
            }

            // Always in agent mode with canvas for code editing (for all /code routes)
            if (location.pathname.startsWith('/code') && !location.search.includes('conversationId')) {
                setAiMode('agent');
            }
        };

        loadProjectFromLocalStorage();
    }, [location.pathname, location.search, params.tenantId, params.projectId, setAiMode]); // Added params to dependencies

    // --- select-project window event (dispatched by Home/Memories) ---
    useEffect(() => {
        const handleSelectProject = async (event) => {
            const { project, projectId } = event.detail || {};

            // Get project ID from either project object or direct projectId parameter
            const selectedProjectId = projectId || project?._id || project?.projectId;

            if (!selectedProjectId) {
                console.error('No project ID provided in select-project event');
                return;
            }

            try {
                devLog('🎯 Selecting project:', selectedProjectId);

                // Show loading toast
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: { message: '🔄 Loading project...', type: 'info' }
                }));

                // Fetch full project info from API
                const projectInfo = await miaPlatformDataService.getProjectInfo(selectedProjectId);

                if (!projectInfo) {
                    throw new Error('Failed to fetch project information');
                }

                // Set up canvas project context
                const newContext = {
                    projectId: selectedProjectId,
                    project: projectInfo,
                    tenant: {
                        id: projectInfo.tenantId,
                        name: projectInfo.tenantName || projectInfo.tenantId
                    }
                };

                // Update canvas project context
                setCanvasProjectContext(newContext);

                // Canvas mode is always enabled, just ensure localStorage is set
                localStorage.setItem('canvasMode', 'true');

                // Store in localStorage for persistence
                localStorage.setItem('canvasProjectContext', JSON.stringify(newContext));
                localStorage.setItem('currentProjectId', selectedProjectId);

                // Success notification
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                        message: `✅ Loaded project: ${projectInfo.name || selectedProjectId}`,
                        type: 'success'
                    }
                }));

                devLog('✅ Project loaded successfully:', projectInfo);

            } catch (error) {
                console.error('Failed to load project:', error);
                window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                        message: `❌ Failed to load project: ${error.message}`,
                        type: 'error'
                    }
                }));
            }
        };

        window.addEventListener('select-project', handleSelectProject);

        return () => {
            window.removeEventListener('select-project', handleSelectProject);
        };
    }, [setCanvasProjectContext]);

    // --- Canvas's onProjectContextChange prop ---
    const handleProjectContextChange = useCallback((context) => {
        setCanvasProjectContext(context);
        // eslint-disable-next-line no-console
        devLog('📋 Canvas project context updated:', context);
    }, []);

    // --- Canvas's onSelectionChange prop: the single write path for URL sync ---
    const syncUrlWithCanvasSelections = useCallback((microservice, branch, tab, filePath, lineNumber) => {
        const tenantId = params.tenantId || canvasProjectContext?.tenant?.tenantId;
        const projectId = params.projectId || canvasProjectContext?.projectId;

        if (!tenantId || !projectId) {
            return;
        }

        // Check if we're switching to this project - if so, don't sync URL with old selections
        const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
        if (switchingToProject === projectId) {
            // eslint-disable-next-line no-console
            devLog('🚫 Skipping URL sync - project switching in progress for:', projectId);
            return;
        }

        // Same service/branch/tab/file: the URL's `#L42` stays (utils/codeRoute.js). The query string (e.g. playbookId,
        // conversationId) always does.
        const target = selectionSyncTarget({
            tenantId, projectId, service: microservice, branch, tab, filePath, line: lineNumber, location,
        });
        if (target) {
            navigate(target, { replace: true });
        }
    }, [params.tenantId, params.projectId, canvasProjectContext, location, navigate]);

    // --- Effect #5: reconstruct URL from localStorage when partial URL is provided ---
    useEffect(() => {
        // Only run when we have tenantId and projectId but missing microservice/branch/tab
        if (!params.tenantId || !params.projectId || params.microservice) return;
        // A link to a project file (`?view=files&path=...`, the chat's file names) asks for Project Home's Files view on
        // purpose: do not send it back to the last service from localStorage (the URL used to change back ~100 ms later).
        if (filesPathFromSearch(location.search)) return;
        // A link to a workspace tab or a Home view (`?view=dashboard&tab=quality`, `?tab=traces`, `?view=logs`) asks for
        // that place: the URL wins over the last service and tab remembered here (it used to land there instead).
        if (viewFromSearch(location.search) || new URLSearchParams(location.search).get('view')) return;

        try {
            // Check if we're switching to this specific project - if so, skip URL reconstruction
            const switchingToProject = sessionStorage.getItem('mia_switching_to_project');
            if (switchingToProject === params.projectId) {
                // eslint-disable-next-line no-console
                devLog('🚫 Skipping URL reconstruction - switching to project:', params.projectId);
                // DON'T clear the flag here - it will be cleared when project is fully loaded
                return;
            }

            // Check localStorage for saved selections
            const savedResource = localStorage.getItem('mia_selectedResource');
            const savedRevision = localStorage.getItem('mia_selectedRevision');
            const savedTab = localStorage.getItem('mia_activeTab');

            if (savedResource || savedRevision || savedTab) {
                let microservice = null;
                let branch = null;
                let tab = null;

                if (savedResource) {
                    const resource = JSON.parse(savedResource);
                    // Validate that the resource belongs to the current project
                    const resourceProjectId = resource?.projectId;
                    if (resourceProjectId && resourceProjectId !== params.projectId) {
                        // Resource is from a different project, clear it and don't use it
                        // eslint-disable-next-line no-console
                        devLog('🔄 Clearing localStorage resource from different project:', resourceProjectId, '!==', params.projectId);
                        localStorage.removeItem('mia_selectedResource');
                        // Don't use this resource
                    } else if (resource?.type === 'service' && resource?.data?.name) {
                        microservice = resource.data.name;
                    }
                }

                if (savedRevision) {
                    const revision = JSON.parse(savedRevision);
                    if (revision?.name) {
                        branch = revision.name;
                    }
                }

                if (savedTab) {
                    tab = savedTab;
                }

                // Reconstruct URL with saved selections (only if we have valid data AND we actually need to change the URL)
                if (microservice || branch || tab) {
                    // Build the expected path
                    let expectedPath = `/code/${params.tenantId}/${params.projectId}`;
                    if (microservice) {
                        expectedPath += `/${encodeServiceNameForUrl(microservice)}`;
                        if (branch) {
                            expectedPath += `/${encodePathSegment(branch)}`;
                            if (tab) {
                                expectedPath += `/${encodePathSegment(tab)}`;
                            }
                        }
                    }

                    // Only navigate if the current path is different from expected path
                    // This prevents unnecessary navigation that would clear conversationId
                    if (location.pathname !== expectedPath) {
                        // Preserve conversationId query parameter if it exists
                        const searchParams = createSearchParams(location.search);
                        const conversationIdParam = searchParams.get('conversationId');
                        let finalPath = expectedPath;
                        if (conversationIdParam) {
                            finalPath += `?conversationId=${conversationIdParam}`;
                        }

                        // eslint-disable-next-line no-console
                        devLog('🔗 Reconstructing URL from localStorage:', finalPath);
                        navigate(finalPath, { replace: true });
                    }
                }
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error reconstructing URL from localStorage:', error);
        }
    }, [params.tenantId, params.projectId, params.microservice, navigate, location.pathname]);

    return {
        canvasProjectContext,
        setCanvasProjectContext,
        urlValidationError,
        setUrlValidationError,
        showProjectSelector,
        setShowProjectSelector,
        lastLoadedProjectRef,
        handleProjectContextChange,
        syncUrlWithCanvasSelections,
    };
}
